<template>
  <div class="material-check-page frame-page qrcode-parse-page">
    <div class="page-intro">
      <p>
        输入文本生成二维码，可用手机扫描识别；也可上传含二维码的图片进行解析。
      </p>
    </div>

    <section class="panel">
      <div class="panel-title panel-title-actions">
        <div class="panel-title-left">
          <span class="step">1</span>
          <span>文本转二维码</span>
          <span v-if="qrDataUrl" class="tag tag-done">已生成</span>
          <span v-else class="tag tag-muted">待生成</span>
        </div>
        <div class="panel-title-right">
          <Button size="s" class="drop-btn-clear" @click="clearGenerate">清除</Button>
          <Button size="s" color="primary" :disabled="!text.trim()" @click="generateQr">生成</Button>
        </div>
      </div>

      <div class="qr-textarea-wrap">
        <textarea
          v-model="text"
          class="qr-textarea"
          rows="10"
          maxlength="400"
          placeholder="输入要生成二维码的文本、链接或编号…"
          @keyup.ctrl.enter="generateQr"
        />
        <div class="qr-char-count">{{ text.length }} / 400</div>
      </div>

      <div v-if="qrDataUrl" class="qr-result">
        <img :src="qrDataUrl" alt="二维码" class="qr-image" />
        <div class="qr-actions">
          <Button size="s" color="primary" @click="downloadQr">下载图片</Button>
          <Button size="s" @click="copyText">复制文本</Button>
        </div>
        <p class="qr-hint">可用手机相机或微信扫一扫识别</p>
      </div>
      <div v-else class="qr-empty">生成后将在此显示二维码</div>
    </section>

    <section class="panel">
      <div class="panel-title panel-title-actions">
        <div class="panel-title-left">
          <span class="step">2</span>
          <span>扫描识别</span>
          <span v-if="decodedText" class="tag tag-done">已识别</span>
          <span v-else class="tag tag-muted">待识别</span>
        </div>
        <div class="panel-title-right">
          <Button size="s" class="drop-btn-clear" @click="clearScan">清除</Button>
          <Button v-if="cameraOn" size="s" color="primary" @click="captureAndDecode">拍照识别</Button>
          <Button size="s" @click="toggleCamera">{{ cameraOn ? '关闭摄像头' : '打开摄像头' }}</Button>
          <Button size="s" :disabled="cameraOn" @click="pickImage">上传图片</Button>
        </div>
      </div>

      <input ref="imageInput" type="file" hidden accept="image/*" @change="onImageSelected" />

      <div
        class="drop-zone scan-zone"
        :class="{ 'is-dragover': dragOver, 'is-filled': !!previewUrl || cameraOn, 'is-camera': cameraOn }"
        @dragenter.prevent="dragOver = true"
        @dragover.prevent="dragOver = true"
        @dragleave.prevent="dragOver = false"
        @drop.prevent="onDrop"
        @click="!cameraOn && !previewUrl && pickImage()"
      >
        <div v-if="cameraOn" class="camera-stage">
          <video ref="videoEl" class="scan-video" autoplay playsinline muted />
          <div class="camera-tip">对准二维码后点击「拍照识别」</div>
        </div>
        <img v-else-if="previewUrl" :src="previewUrl" alt="待识别图片" class="scan-preview" />
        <div v-else class="drop-zone-body drop-zone-body-compact">
          <div class="drop-icon"><span>扫码</span></div>
          <div class="drop-text">
            <div class="drop-title placeholder">选择或拖拽二维码图片</div>
            <div class="drop-hint">支持 jpg / png / webp；也可打开摄像头拍照后识别</div>
          </div>
        </div>
      </div>

      <canvas ref="scanCanvas" class="scan-canvas-hidden" />

      <div v-if="decodedText" class="decode-result">
        <div class="decode-label">识别结果</div>
        <pre class="decode-text">{{ decodedText }}</pre>
        <div class="qr-actions">
          <Button size="s" color="primary" @click="copyDecoded">复制结果</Button>
          <Button v-if="isHttpUrl(decodedText)" size="s" @click="openDecoded">打开链接</Button>
        </div>
      </div>
      <div v-else-if="scanError" class="decode-error">{{ scanError }}</div>
    </section>
  </div>
</template>

<script setup>
import { nextTick, onBeforeUnmount, ref } from 'vue';
import QRCode from 'qrcode';
import jsQR from 'jsqr';
import { message } from 'heyui';
import '@/views/material-check/material-check.less';

const text = ref('');
const qrDataUrl = ref('');
const previewUrl = ref('');
const decodedText = ref('');
const scanError = ref('');
const dragOver = ref(false);
const cameraOn = ref(false);
const imageInput = ref(null);
const videoEl = ref(null);
const scanCanvas = ref(null);

let mediaStream = null;

async function generateQr() {
  const value = text.value.trim();
  if (!value) {
    message.warn('请先输入文本');
    return;
  }
  try {
    qrDataUrl.value = await QRCode.toDataURL(value, {
      width: 280,
      margin: 2,
      errorCorrectionLevel: 'M',
      color: { dark: '#111827', light: '#ffffff' },
    });
  } catch (err) {
    message.error('生成失败：' + (err.message || '未知错误'));
  }
}

function clearGenerate() {
  text.value = '';
  qrDataUrl.value = '';
}

function downloadQr() {
  if (!qrDataUrl.value) return;
  const a = document.createElement('a');
  a.href = qrDataUrl.value;
  a.download = `qrcode_${Date.now()}.png`;
  a.click();
}

async function copyText() {
  const value = text.value.trim();
  if (!value) return;
  try {
    await navigator.clipboard.writeText(value);
    message.success('已复制文本');
  } catch (e) {
    message.error('复制失败');
  }
}

function pickImage() {
  imageInput.value && imageInput.value.click();
}

function onImageSelected(e) {
  const file = e.target.files && e.target.files[0];
  e.target.value = '';
  if (file) handleImageFile(file);
}

function onDrop(e) {
  dragOver.value = false;
  if (cameraOn.value) return;
  const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
  if (file) handleImageFile(file);
}

async function handleImageFile(file) {
  if (!file.type || !file.type.startsWith('image/')) {
    message.error('请选择图片文件');
    return;
  }
  await stopCamera();
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value);
  previewUrl.value = URL.createObjectURL(file);
  decodedText.value = '';
  scanError.value = '';
  await nextTick();
  await decodeFromUrl(previewUrl.value);
}

function decodeFromUrl(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = scanCanvas.value;
      if (!canvas) {
        resolve(false);
        return;
      }
      const ctx = canvas.getContext('2d');
      const maxSide = 1200;
      let w = img.naturalWidth || img.width;
      let h = img.naturalHeight || img.height;
      if (Math.max(w, h) > maxSide) {
        const scale = maxSide / Math.max(w, h);
        w = Math.round(w * scale);
        h = Math.round(h * scale);
      }
      canvas.width = w;
      canvas.height = h;
      ctx.drawImage(img, 0, 0, w, h);
      const imageData = ctx.getImageData(0, 0, w, h);
      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'attemptBoth',
      });
      if (code && code.data) {
        decodedText.value = code.data;
        scanError.value = '';
        message.success('识别成功');
        resolve(true);
      } else {
        decodedText.value = '';
        scanError.value = '未识别到二维码，请换一张更清晰的图片';
        resolve(false);
      }
    };
    img.onerror = () => {
      scanError.value = '图片加载失败';
      resolve(false);
    };
    img.src = url;
  });
}

async function toggleCamera() {
  if (cameraOn.value) {
    await stopCamera();
    return;
  }
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    message.error('当前环境不支持摄像头');
    return;
  }
  try {
    if (previewUrl.value) {
      URL.revokeObjectURL(previewUrl.value);
      previewUrl.value = '';
    }
    decodedText.value = '';
    scanError.value = '';
    mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: {
        facingMode: { ideal: 'environment' },
        width: { ideal: 1280 },
        height: { ideal: 720 },
      },
    });
    cameraOn.value = true;
    await nextTick();
    if (videoEl.value) {
      videoEl.value.srcObject = mediaStream;
      await videoEl.value.play();
    }
  } catch (err) {
    cameraOn.value = false;
    message.error('无法打开摄像头：' + (err.message || '请检查权限'));
  }
}

async function captureAndDecode() {
  const video = videoEl.value;
  const canvas = scanCanvas.value;
  if (!cameraOn.value || !video || !canvas) {
    message.warn('请先打开摄像头');
    return;
  }
  if (video.readyState < 2 || !video.videoWidth) {
    message.warn('摄像头尚未就绪，请稍候再拍');
    return;
  }

  const w = video.videoWidth;
  const h = video.videoHeight;
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(video, 0, 0, w, h);

  const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
  await stopCamera();
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value);
  previewUrl.value = dataUrl;
  decodedText.value = '';
  scanError.value = '';
  await nextTick();

  const imageData = ctx.getImageData(0, 0, w, h);
  const code = jsQR(imageData.data, imageData.width, imageData.height, {
    inversionAttempts: 'attemptBoth',
  });
  if (code && code.data) {
    decodedText.value = code.data;
    scanError.value = '';
    message.success('识别成功');
  } else {
    // 再走一遍缩放识别，提高成功率
    const ok = await decodeFromUrl(dataUrl);
    if (!ok) {
      scanError.value = '未识别到二维码，请重新拍照或换张更清晰的图片';
    }
  }
}

async function stopCamera() {
  if (mediaStream) {
    mediaStream.getTracks().forEach((t) => t.stop());
    mediaStream = null;
  }
  if (videoEl.value) {
    videoEl.value.srcObject = null;
  }
  cameraOn.value = false;
}

function clearScan() {
  stopCamera();
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value);
  previewUrl.value = '';
  decodedText.value = '';
  scanError.value = '';
}

function isHttpUrl(value) {
  return /^https?:\/\//i.test(String(value || '').trim());
}

async function copyDecoded() {
  if (!decodedText.value) return;
  try {
    await navigator.clipboard.writeText(decodedText.value);
    message.success('已复制识别结果');
  } catch (e) {
    message.error('复制失败');
  }
}

function openDecoded() {
  if (!isHttpUrl(decodedText.value)) return;
  window.open(decodedText.value, '_blank', 'noopener,noreferrer');
}

onBeforeUnmount(() => {
  clearScan();
});
</script>

<style lang="less" scoped>
.qr-textarea-wrap {
  position: relative;
}

.qr-textarea {
  width: 100%;
  box-sizing: border-box;
  resize: vertical;
  min-height: 220px;
  padding: 12px 14px 32px;
  border: 1px solid #e8ecf1;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.6;
  color: #1f2937;
  outline: none;
  font-family: inherit;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
  }
}

.qr-char-count {
  position: absolute;
  right: 12px;
  bottom: 10px;
  font-size: 12px;
  color: #8a94a6;
  pointer-events: none;
  line-height: 1;
}

.qr-result {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.qr-image {
  width: 280px;
  height: 280px;
  border: 1px solid #e8ecf1;
  border-radius: 12px;
  background: #fff;
  padding: 8px;
  box-sizing: border-box;
}

.qr-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
}

.qr-hint,
.qr-empty {
  margin: 16px 0 0;
  text-align: center;
  color: #8a94a6;
  font-size: 13px;
}

.scan-zone {
  min-height: 220px;
  cursor: pointer;
  overflow: hidden;

  &.is-camera {
    cursor: default;
    padding: 0;
    background: #0f172a;
  }
}

.camera-stage {
  position: relative;
  width: 100%;
  height: min(52vh, 420px);
  background: #0f172a;
}

.scan-preview {
  display: block;
  width: 100%;
  max-height: 420px;
  object-fit: contain;
  background: #0f172a;
}

.scan-video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #0f172a;
}

.camera-tip {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 10px 12px;
  text-align: center;
  font-size: 13px;
  color: #fff;
  background: linear-gradient(transparent, rgba(15, 23, 42, 0.75));
  pointer-events: none;
}

.scan-canvas-hidden {
  display: none;
}

.decode-result {
  margin-top: 16px;
  padding: 14px 16px;
  background: #f7f9fc;
  border: 1px solid #e8ecf1;
  border-radius: 8px;
}

.decode-label {
  font-size: 13px;
  color: #8a94a6;
  margin-bottom: 8px;
}

.decode-text {
  margin: 0 0 12px;
  white-space: pre-wrap;
  word-break: break-all;
  font-size: 14px;
  line-height: 1.6;
  color: #1f2937;
  font-family: inherit;
}

.decode-error {
  margin-top: 14px;
  color: #dc2626;
  font-size: 13px;
}
</style>
